import { z } from 'zod';
import { PRODUCT_TYPE_CODES } from './product-types.js';
import {
  DEFAULT_INVOICE_CURRENCY,
  DEFAULT_INVOICE_TERMS,
  INVOICE_GENERATION_STATUSES,
  INVOICE_FEEDBACK_LINK_STATUSES,
  INVOICE_SOURCE_TYPES,
  INVOICE_STATUSES,
  REQUEST_STATUSES,
  MAX_INVOICE_DESCRIPTION_CHARS,
  MAX_INVOICE_LINE_ITEMS,
  MAX_INVOICE_NOTE_CHARS,
  MAX_PAYMENT_NOTE_CHARS,
  MAX_VOID_REASON_CHARS,
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
} from './enums.js';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => (value ? value : undefined));

const minorUnitsSchema = z.number().int().min(0).max(99_999_999);

export const invoiceCustomerSnapshotSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    email: optionalText(254),
    phone: optionalText(40),
    billingAddress: optionalText(400),
    shippingAddress: optionalText(400),
  })
  .strict();

export const invoiceBusinessSnapshotSchema = z
  .object({
    legalName: z.string().trim().min(1).max(160),
    website: z.string().trim().min(1).max(160),
    contactEmail: optionalText(254),
    timezone: z.string().trim().min(1).max(80),
  })
  .strict();

export const invoiceDraftLineInputSchema = z
  .object({
    description: z.string().trim().min(1).max(MAX_INVOICE_DESCRIPTION_CHARS),
    quantity: z.number().int().min(1).max(999),
    unitPriceMinor: minorUnitsSchema,
    discountMinor: minorUnitsSchema.optional(),
    productType: z.enum(PRODUCT_TYPE_CODES).optional(),
    contentsNote: optionalText(400),
  })
  .strict();

export const invoiceLineItemSchema = invoiceDraftLineInputSchema.extend({
  lineNetMinor: z.number().int(),
});

export const createInvoiceDraftBodySchema = z
  .object({
    sourceType: z.literal('MANUAL'),
    sourceOrderNumber: optionalText(80),
    customer: invoiceCustomerSnapshotSchema,
    lineItems: z.array(invoiceDraftLineInputSchema).min(1).max(MAX_INVOICE_LINE_ITEMS),
    discountMinor: minorUnitsSchema.optional(),
    shippingMinor: minorUnitsSchema.optional(),
    taxMinor: minorUnitsSchema.optional(),
    taxConfirmed: z.boolean().optional(),
    currency: z.literal(DEFAULT_INVOICE_CURRENCY).optional(),
    dueDate: optionalText(32),
    customerNote: optionalText(MAX_INVOICE_NOTE_CHARS),
    terms: optionalText(200),
    includeFeedbackRequest: z.boolean().optional(),
  })
  .strict();

export const updateInvoiceDraftBodySchema = createInvoiceDraftBodySchema.partial().strict();

export const invoiceTotalsSchema = z.object({
  subtotalMinor: z.number().int(),
  discountMinor: z.number().int(),
  shippingMinor: z.number().int(),
  taxMinor: z.number().int(),
  totalMinor: z.number().int(),
  currency: z.literal(DEFAULT_INVOICE_CURRENCY),
  calculationVersion: z.number().int(),
});

export const adminInvoiceSchema = z.object({
  invoiceId: z.string().uuid(),
  invoiceNumber: z.string().optional(),
  sequenceYear: z.number().int().optional(),
  sequenceNumber: z.number().int().optional(),
  status: z.enum(INVOICE_STATUSES),
  sourceType: z.enum(INVOICE_SOURCE_TYPES),
  sourceOrderNumber: z.string().optional(),
  customer: invoiceCustomerSnapshotSchema,
  business: invoiceBusinessSnapshotSchema.optional(),
  lineItems: z.array(invoiceLineItemSchema),
  subtotalMinor: z.number().int(),
  discountMinor: z.number().int(),
  shippingMinor: z.number().int(),
  taxMinor: z.number().int(),
  totalMinor: z.number().int(),
  currency: z.literal(DEFAULT_INVOICE_CURRENCY),
  dueDate: z.string().optional(),
  issueDate: z.string().optional(),
  issuedAt: z.string().optional(),
  customerNote: z.string().optional(),
  terms: z.string().optional(),
  includeFeedbackRequest: z.boolean(),
  taxConfirmed: z.boolean(),
  calculationVersion: z.number().int(),
  templateVersion: z.number().int().optional(),
  internalGenerationStatus: z.enum(INVOICE_GENERATION_STATUSES).optional(),
  pdfSha256: z.string().optional(),
  paidMinor: z.number().int().min(0),
  creditedMinor: z.number().int().min(0),
  balanceDueMinor: z.number().int(),
  paidAt: z.string().optional(),
  voidedAt: z.string().optional(),
  voidReason: z.string().optional(),
  lastSentAt: z.string().optional(),
  shareExpiresAt: z.string().optional(),
  feedbackRequestId: z.string().uuid().optional(),
  feedbackGenerationStatus: z.enum(INVOICE_FEEDBACK_LINK_STATUSES).optional(),
  feedbackStatus: z.enum(REQUEST_STATUSES).optional(),
  feedbackExpiresAt: z.number().int().optional(),
  feedbackSubmitted: z.boolean().optional(),
  feedbackOverallRating: z.number().int().min(1).max(5).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
});

export const recordInvoicePaymentBodySchema = z
  .object({
    amountMinor: z.number().int().positive().max(99_999_999),
    currency: z.literal(DEFAULT_INVOICE_CURRENCY).optional(),
    method: z.enum(PAYMENT_METHODS),
    receivedOn: optionalText(32),
    reference: optionalText(80),
    note: optionalText(MAX_PAYMENT_NOTE_CHARS),
  })
  .strict();

export const voidPaymentBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(MAX_VOID_REASON_CHARS),
  })
  .strict();

export const refundPaymentBodySchema = voidPaymentBodySchema;

export const voidInvoiceBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(MAX_VOID_REASON_CHARS),
  })
  .strict();

export const createCreditNoteBodySchema = z
  .object({
    amountMinor: z.number().int().positive().max(99_999_999),
    reason: z.string().trim().min(1).max(MAX_VOID_REASON_CHARS),
  })
  .strict();

export const adminPaymentSchema = z.object({
  paymentId: z.string().uuid(),
  invoiceId: z.string().uuid(),
  invoiceNumber: z.string().optional(),
  amountMinor: z.number().int().positive(),
  currency: z.literal(DEFAULT_INVOICE_CURRENCY),
  method: z.enum(PAYMENT_METHODS),
  status: z.enum(PAYMENT_STATUSES),
  receivedOn: z.string(),
  createdAt: z.string(),
  recordedBy: z.string(),
  voidedAt: z.string().optional(),
});

export const listPaymentsResponseSchema = z.object({
  items: z.array(adminPaymentSchema),
});

export const recordPaymentResponseSchema = z.object({
  payment: adminPaymentSchema,
  invoice: adminInvoiceSchema,
});

export const adminCreditNoteSchema = z.object({
  creditNoteId: z.string().uuid(),
  creditNoteNumber: z.string(),
  invoiceId: z.string().uuid(),
  invoiceNumber: z.string().optional(),
  amountMinor: z.number().int().positive(),
  currency: z.literal(DEFAULT_INVOICE_CURRENCY),
  reason: z.string(),
  issuedAt: z.string(),
  issueDate: z.string(),
  pdfReady: z.boolean(),
  createdAt: z.string(),
});

export const creditNotePdfUrlResponseSchema = z.object({
  url: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
  creditNoteNumber: z.string(),
});

export const sendInvoiceBodySchema = z
  .object({
    toEmail: optionalText(254),
  })
  .strict();

export const invoiceShareLinkResponseSchema = z.object({
  url: z.string().min(1).optional(),
  whatsappUrl: z.string().min(1).optional(),
  expiresAt: z.string(),
  replayed: z.boolean(),
});

export const sendInvoiceResponseSchema = invoiceShareLinkResponseSchema.extend({
  emailed: z.boolean(),
  invoice: adminInvoiceSchema,
});

export const publicInvoiceLineSchema = z.object({
  description: z.string(),
  quantity: z.number().int(),
  unitPriceMinor: z.number().int(),
  lineNetMinor: z.number().int(),
});

export const publicInvoiceSchema = z.object({
  invoiceNumber: z.string(),
  issueDate: z.string().optional(),
  dueDate: z.string().optional(),
  status: z.enum(INVOICE_STATUSES),
  customerName: z.string(),
  lineItems: z.array(publicInvoiceLineSchema),
  subtotalMinor: z.number().int(),
  discountMinor: z.number().int(),
  shippingMinor: z.number().int(),
  taxMinor: z.number().int(),
  totalMinor: z.number().int(),
  paidMinor: z.number().int(),
  creditedMinor: z.number().int(),
  balanceDueMinor: z.number().int(),
  currency: z.literal(DEFAULT_INVOICE_CURRENCY),
  terms: z.string().optional(),
  businessName: z.string(),
  website: z.string(),
  expiresAt: z.string(),
});

export const invoiceGenerationStatusResponseSchema = z.object({
  invoiceId: z.string().uuid(),
  invoiceNumber: z.string().optional(),
  status: z.enum(INVOICE_STATUSES),
  internalGenerationStatus: z.enum(INVOICE_GENERATION_STATUSES).optional(),
  pdfReady: z.boolean(),
});

export const invoicePdfUrlResponseSchema = z.object({
  url: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
  invoiceNumber: z.string().optional(),
  watermark: z.enum(['DRAFT', 'NONE']),
});

export const listInvoicesResponseSchema = z.object({
  items: z.array(adminInvoiceSchema),
  nextCursor: z.string().nullable(),
});

export const invoiceCatalogItemSchema = z.object({
  productType: z.enum(PRODUCT_TYPE_CODES),
  displayName: z.string(),
  unitPriceMinor: z.number().int(),
  developmentOnly: z.literal(true),
});

export const DEFAULT_INVOICE_CATALOG = [
  {
    productType: 'GANESH_POOJA_BOX',
    displayName: 'Ganesh Pooja Gift Box',
    unitPriceMinor: 3999,
    developmentOnly: true,
  },
  {
    productType: 'LAKSHMI_FESTIVE_BOX',
    displayName: 'Lakshmi Festive Gift Box',
    unitPriceMinor: 4999,
    developmentOnly: true,
  },
] as const;

export { DEFAULT_INVOICE_TERMS };

export type InvoiceCustomerSnapshot = z.infer<typeof invoiceCustomerSnapshotSchema>;
export type InvoiceBusinessSnapshot = z.infer<typeof invoiceBusinessSnapshotSchema>;
export type InvoiceDraftLineInput = z.infer<typeof invoiceDraftLineInputSchema>;
export type InvoiceLineItem = z.infer<typeof invoiceLineItemSchema>;
export type CreateInvoiceDraftBody = z.infer<typeof createInvoiceDraftBodySchema>;
export type UpdateInvoiceDraftBody = z.infer<typeof updateInvoiceDraftBodySchema>;
export type InvoiceTotals = z.infer<typeof invoiceTotalsSchema>;
export type AdminInvoice = z.infer<typeof adminInvoiceSchema>;
export type ListInvoicesResponse = z.infer<typeof listInvoicesResponseSchema>;
export type InvoiceGenerationStatusResponse = z.infer<typeof invoiceGenerationStatusResponseSchema>;
export type InvoicePdfUrlResponse = z.infer<typeof invoicePdfUrlResponseSchema>;
export type RecordInvoicePaymentBody = z.infer<typeof recordInvoicePaymentBodySchema>;
export type VoidPaymentBody = z.infer<typeof voidPaymentBodySchema>;
export type RefundPaymentBody = z.infer<typeof refundPaymentBodySchema>;
export type VoidInvoiceBody = z.infer<typeof voidInvoiceBodySchema>;
export type CreateCreditNoteBody = z.infer<typeof createCreditNoteBodySchema>;
export type AdminPayment = z.infer<typeof adminPaymentSchema>;
export type ListPaymentsResponse = z.infer<typeof listPaymentsResponseSchema>;
export type RecordPaymentResponse = z.infer<typeof recordPaymentResponseSchema>;
export type AdminCreditNote = z.infer<typeof adminCreditNoteSchema>;
export type CreditNotePdfUrlResponse = z.infer<typeof creditNotePdfUrlResponseSchema>;
export type SendInvoiceBody = z.infer<typeof sendInvoiceBodySchema>;
export type InvoiceShareLinkResponse = z.infer<typeof invoiceShareLinkResponseSchema>;
export type SendInvoiceResponse = z.infer<typeof sendInvoiceResponseSchema>;
export type PublicInvoice = z.infer<typeof publicInvoiceSchema>;
